import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { crossTenantStyles as styles, crossTenantStatusStyle } from './crossTenantStyles';

/**
 * Shared collapsible, org-grouped left-pane list for the operator console's
 * cross-tenant views (Events, Giving, and future cross-tenant pages).
 *
 * Groups rows by organization into collapsible sections so the operator can
 * manage the view as the platform scales to many orgs. Behavior:
 *  - Orgs sorted alphabetically; each header shows the org name + item count.
 *  - Smart default: collapsed when there are 6+ orgs, expanded otherwise.
 *  - "Expand all / Collapse all" toggle in the toolbar.
 *  - Search-aware: force-expands all groups when `forceExpand` is set.
 *  - Selecting an item calls onSelect; the active item is highlighted.
 *
 * Keyboard navigation (focus the list, then):
 *  - ↑ / ↓        move the highlight between visible item rows (scrolls into view)
 *  - Enter / Space open the highlighted item
 *  - ← / →        collapse / expand the org group the highlight is in
 *  - Home / End   jump to the first / last visible item
 * Uses a roving tabindex so the list is a single tab stop, then arrows move within.
 *
 * Generic over the row type. The caller supplies accessors so this component
 * stays presentation-only and reusable across pages.
 */

export interface GroupedRow {
  id: string;
  tenantId: string;
  tenantName: string;
}

interface CrossTenantGroupedListProps<T extends GroupedRow> {
  rows: T[];
  selectedId: string | null;
  onSelect: (row: T) => void;
  /** Primary line for a row (e.g. event/campaign name). */
  renderTitle: (row: T) => React.ReactNode;
  /** Secondary muted meta line under the title. */
  renderMeta: (row: T) => React.ReactNode;
  /** Right-aligned trailing value (e.g. guest count, raised amount). */
  renderTrailing: (row: T) => React.ReactNode;
  /** Status string used for the pill shown next to the trailing value. */
  statusOf?: (row: T) => string;
  /** Noun for the toolbar count, e.g. "event" / "campaign". */
  itemNoun: string;
  /** Message when there are no rows after filtering. */
  emptyLabel: string;
  /** When true (e.g. an active search), force every group open regardless of toggle state. */
  forceExpand?: boolean;
}

const SMART_COLLAPSE_THRESHOLD = 6;

export function CrossTenantGroupedList<T extends GroupedRow>({
  rows,
  selectedId,
  onSelect,
  renderTitle,
  renderMeta,
  renderTrailing,
  statusOf,
  itemNoun,
  emptyLabel,
  forceExpand,
}: CrossTenantGroupedListProps<T>) {
  // Group rows by tenant, orgs alphabetical, preserving row order within a group.
  const groups = useMemo(() => {
    const byTenant = new Map<string, { tenantId: string; tenantName: string; items: T[] }>();
    for (const r of rows) {
      const key = r.tenantId || '__none__';
      let g = byTenant.get(key);
      if (!g) {
        g = { tenantId: r.tenantId, tenantName: r.tenantName || '(no organization)', items: [] };
        byTenant.set(key, g);
      }
      g.items.push(r);
    }
    return [...byTenant.values()].sort((a, b) =>
      a.tenantName.localeCompare(b.tenantName, undefined, { sensitivity: 'base' }),
    );
  }, [rows]);

  // Manually toggled open/closed state, keyed by tenant. Undefined = follow the
  // smart default. forceExpand overrides both (without mutating state).
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const smartDefaultOpen = groups.length > 0 && groups.length < SMART_COLLAPSE_THRESHOLD;

  // Reset manual toggles when the set of orgs changes (e.g. after data load).
  useEffect(() => {
    setOpenMap({});
  }, [groups.map((g) => g.tenantId).join('|')]);

  const isOpen = useCallback(
    (tenantId: string): boolean => {
      if (forceExpand) return true;
      const key = tenantId || '__none__';
      return key in openMap ? openMap[key] : smartDefaultOpen;
    },
    [forceExpand, openMap, smartDefaultOpen],
  );

  function toggle(tenantId: string) {
    const key = tenantId || '__none__';
    setOpenMap((m) => ({ ...m, [key]: !(key in m ? m[key] : smartDefaultOpen) }));
  }

  function openGroup(tenantId: string, open: boolean) {
    const key = tenantId || '__none__';
    setOpenMap((m) => ({ ...m, [key]: open }));
  }

  function setAll(open: boolean) {
    const next: Record<string, boolean> = {};
    for (const g of groups) next[g.tenantId || '__none__'] = open;
    setOpenMap(next);
  }

  const allOpen = groups.length > 0 && groups.every((g) => isOpen(g.tenantId));
  const totalItems = rows.length;

  // Flattened list of currently-visible item rows (under open groups), in
  // display order — this is what the arrow keys traverse.
  const visibleItems = useMemo(() => {
    const flat: { row: T; tenantId: string }[] = [];
    for (const g of groups) {
      if (isOpen(g.tenantId)) {
        for (const row of g.items) flat.push({ row, tenantId: g.tenantId });
      }
    }
    return flat;
  }, [groups, isOpen]);

  // Roving highlight index into visibleItems; -1 = none yet.
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const itemRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Keep the highlight pointed at the selected item when selection changes
  // externally (e.g. deep-link) and when the visible set changes.
  useEffect(() => {
    if (selectedId == null) return;
    const idx = visibleItems.findIndex((v) => v.row.id === selectedId);
    if (idx >= 0) setActiveIndex(idx);
  }, [selectedId, visibleItems]);

  // Clamp the highlight if the visible set shrank.
  useEffect(() => {
    if (activeIndex >= visibleItems.length) setActiveIndex(visibleItems.length - 1);
  }, [visibleItems.length, activeIndex]);

  function focusItem(idx: number) {
    const v = visibleItems[idx];
    if (!v) return;
    const el = itemRefs.current.get(v.row.id);
    el?.scrollIntoView({ block: 'nearest' });
    el?.focus();
  }

  function moveTo(idx: number) {
    const clamped = Math.max(0, Math.min(idx, visibleItems.length - 1));
    setActiveIndex(clamped);
    focusItem(clamped);
  }

  function onItemKeyDown(e: React.KeyboardEvent, idx: number) {
    const v = visibleItems[idx];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveTo(idx + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveTo(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        moveTo(0);
        break;
      case 'End':
        e.preventDefault();
        moveTo(visibleItems.length - 1);
        break;
      case 'ArrowLeft':
        // Collapse this item's group; focus moves to that group header.
        e.preventDefault();
        if (v && !forceExpand) {
          openGroup(v.tenantId, false);
          const key = v.tenantId || '__none__';
          requestAnimationFrame(() => groupHeaderRefs.current.get(key)?.focus());
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (v) onSelect(v.row);
        break;
    }
  }

  const groupHeaderRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  function onHeaderKeyDown(e: React.KeyboardEvent, tenantId: string, firstItemIndex: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        if (!isOpen(tenantId)) openGroup(tenantId, true);
        else if (firstItemIndex >= 0) moveTo(firstItemIndex); // already open → into first child
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (isOpen(tenantId) && !forceExpand) openGroup(tenantId, false);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        toggle(tenantId);
        break;
    }
  }

  if (totalItems === 0) {
    return <div style={styles.emptyCell}>{emptyLabel}</div>;
  }

  // Running index across the flattened visible items, to map each rendered row
  // back to its position for roving tabindex + key handling.
  let runningIndex = -1;

  return (
    <div role="tree" aria-label={`${itemNoun} list grouped by organization`}>
      <div style={styles.groupToolbar}>
        <span style={styles.groupToolbarCount}>
          {totalItems} {itemNoun}{totalItems === 1 ? '' : 's'} · {groups.length} org{groups.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          style={styles.groupToolbarBtn}
          onClick={() => setAll(!allOpen)}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {groups.map((g) => {
        const open = isOpen(g.tenantId);
        const key = g.tenantId || '__none__';
        // Index of this group's first visible item (for header ArrowRight).
        const firstItemIndex = open ? runningIndex + 1 : -1;
        return (
          <div key={key} role="treeitem" aria-expanded={open}>
            <button
              type="button"
              ref={(el) => groupHeaderRefs.current.set(key, el)}
              style={styles.groupHeader}
              onClick={() => toggle(g.tenantId)}
              onKeyDown={(e) => onHeaderKeyDown(e, g.tenantId, firstItemIndex)}
              aria-expanded={open}
            >
              <span style={{ ...styles.groupCaret, transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
              <span style={styles.groupOrgName}>{g.tenantName}</span>
              <span style={styles.groupCountBadge}>{g.items.length}</span>
            </button>
            {open && (
              <div role="group">
                {g.items.map((row) => {
                  runningIndex += 1;
                  const idx = runningIndex;
                  const status = statusOf?.(row);
                  const isActive = idx === activeIndex;
                  return (
                    <div
                      key={row.id}
                      ref={(el) => itemRefs.current.set(row.id, el)}
                      role="treeitem"
                      aria-selected={row.id === selectedId}
                      tabIndex={isActive || (activeIndex === -1 && idx === 0) ? 0 : -1}
                      onClick={() => { setActiveIndex(idx); onSelect(row); }}
                      onKeyDown={(e) => onItemKeyDown(e, idx)}
                      onFocus={() => setActiveIndex(idx)}
                      style={{
                        ...styles.groupItem,
                        ...(row.id === selectedId ? styles.groupItemActive : {}),
                        ...(isActive ? styles.groupItemFocused : {}),
                      }}
                    >
                      <div style={styles.groupItemMain}>
                        <div style={styles.eName}>{renderTitle(row)}</div>
                        <div style={styles.eMeta}>{renderMeta(row)}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                        {status && <span style={{ ...styles.pill, ...crossTenantStatusStyle(status) }}>{status}</span>}
                        <span style={styles.groupItemMeta}>{renderTrailing(row)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
